import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Bump this (e.g. the current route) to auto-reset after navigation. */
  resetKey?: string;
}
interface State {
  error: Error | null;
  chunkError: boolean;
}

/** True for a failed lazy-chunk load — common right after a deploy when a
 * client is holding a stale index.html that points at hashed chunks that 404. */
function isChunkLoadError(err: Error): boolean {
  return /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
    `${err.name} ${err.message}`,
  );
}

/**
 * Catches render-time errors (including a lazy route chunk that fails to load)
 * so a single throw shows a recoverable message instead of blanking the whole
 * embedded app to white. Resets automatically when `resetKey` changes.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, chunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, chunkError: isChunkLoadError(error) };
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, chunkError: false });
    }
  }

  render() {
    const { error, chunkError } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', maxWidth: 520, margin: '0 auto' }}>
        <h2 style={{ fontSize: 18, margin: '0 0 8px' }}>
          {chunkError ? 'A new version is available' : 'Something went wrong'}
        </h2>
        <p style={{ color: 'var(--s-color-text-secondary, #6b7177)', margin: '0 0 20px', fontSize: 14 }}>
          {chunkError
            ? 'This page was updated since you loaded it. Reload to get the latest version.'
            : 'An unexpected error occurred while rendering this page. Reloading usually fixes it.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            padding: '10px 18px', borderRadius: 8, border: '1px solid var(--s-color-border, #c9cccf)',
            background: 'var(--s-color-bg-surface, #fff)', color: 'inherit',
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
